import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { adminAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Users,
  UserPlus,
  UserMinus,
  UserCog,
  Search,
  MoreVertical,
  Loader2,
  ArrowLeft,
  ArrowRightLeft,
  Shield,
  ShieldCheck,
  Film,
  Mail,
  Phone,
} from "lucide-react";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const roleColors = {
  user: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  account_manager: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  admin: "bg-purple-500/20 text-purple-400 border-purple-500/30",
};

export default function AccountManagerAdminPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("managers");
  
  // Account Managers state
  const [accountManagers, setAccountManagers] = useState([]);
  const [unassignedClients, setUnassignedClients] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Dialog states
  const [assignDialog, setAssignDialog] = useState(null);
  const [transferDialog, setTransferDialog] = useState(null);
  const [roleDialog, setRoleDialog] = useState(null);
  const [selectedManager, setSelectedManager] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const fetchAccountManagers = useCallback(async () => {
    try {
      const response = await adminAPI.getAccountManagers();
      setAccountManagers(response.data.account_managers || []);
    } catch (error) {
      console.error("Failed to fetch account managers:", error);
      toast.error("Failed to load account managers");
    }
  }, []);

  const fetchUnassignedClients = useCallback(async () => {
    try {
      const response = await adminAPI.getUnassignedClients();
      setUnassignedClients(response.data.unassigned_clients || []);
    } catch (error) {
      console.error("Failed to fetch unassigned clients:", error);
    }
  }, []);

  const fetchAllUsers = useCallback(async () => {
    try {
      const response = await adminAPI.getUsers(0, 500);
      setAllUsers(response.data || []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchAccountManagers(),
        fetchUnassignedClients(),
        fetchAllUsers(),
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchAccountManagers, fetchUnassignedClients, fetchAllUsers]);

  const handleAssignClient = async () => {
    if (!assignDialog || !selectedManager) return;
    setActionLoading(true);
    try {
      await adminAPI.assignClient(assignDialog.id, selectedManager);
      toast.success("Client assigned successfully");
      setAssignDialog(null);
      setSelectedManager("");
      fetchUnassignedClients();
      fetchAccountManagers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign client");
    } finally {
      setActionLoading(false);
    }
  };

  const handleTransferClient = async () => {
    if (!transferDialog || !selectedManager) return;
    setActionLoading(true);
    try {
      await adminAPI.transferClient(
        transferDialog.clientId,
        transferDialog.fromManagerId,
        selectedManager
      );
      toast.success("Client transferred successfully");
      setTransferDialog(null);
      setSelectedManager("");
      fetchAccountManagers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to transfer client");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveAssignment = async (clientId) => {
    setActionLoading(true);
    try {
      await adminAPI.removeClientAssignment(clientId);
      toast.success("Client assignment removed");
      fetchUnassignedClients();
      fetchAccountManagers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove assignment");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!roleDialog) return;
    setActionLoading(true);
    try {
      await adminAPI.updateUserRole(roleDialog.userId, roleDialog.newRole);
      toast.success(`User role updated to ${roleDialog.newRole}`);
      setRoleDialog(null);
      fetchAllUsers();
      fetchAccountManagers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    } finally {
      setActionLoading(false);
    }
  };

  const filteredUsers = allUsers.filter(
    (user) =>
      user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      className="min-h-screen bg-background p-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <motion.div variants={itemVariants} className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/admin")}
              data-testid="back-to-admin"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <UserCog className="w-8 h-8 text-primary" />
                Account Manager Administration
              </h1>
              <p className="text-muted-foreground mt-1">
                Manage account managers, assign clients, and control access
              </p>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <ShieldCheck className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{accountManagers.length}</p>
                  <p className="text-sm text-muted-foreground">Account Managers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/20">
                  <Users className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {accountManagers.reduce((sum, am) => sum + am.assigned_clients_count, 0)}
                  </p>
                  <p className="text-sm text-muted-foreground">Assigned Clients</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/20">
                  <UserPlus className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{unassignedClients.length}</p>
                  <p className="text-sm text-muted-foreground">Unassigned Clients</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/20">
                  <Film className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {accountManagers.reduce((sum, am) => sum + am.total_videos_created, 0)}
                  </p>
                  <p className="text-sm text-muted-foreground">Videos Created for Clients</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-secondary/50">
            <TabsTrigger value="managers" className="gap-2">
              <ShieldCheck className="w-4 h-4" />
              Account Managers ({accountManagers.length})
            </TabsTrigger>
            <TabsTrigger value="unassigned" className="gap-2">
              <UserPlus className="w-4 h-4" />
              Unassigned Clients ({unassignedClients.length})
            </TabsTrigger>
            <TabsTrigger value="all-users" className="gap-2">
              <Users className="w-4 h-4" />
              All Users ({allUsers.length})
            </TabsTrigger>
          </TabsList>

          {/* Account Managers Tab */}
          <TabsContent value="managers" className="space-y-4">
            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle>Account Managers</CardTitle>
                <CardDescription>
                  Users with account manager or admin roles who can manage clients
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50 hover:bg-transparent">
                      <TableHead>Manager</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Assigned Clients</TableHead>
                      <TableHead>Videos Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accountManagers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          No account managers found. Promote a user to get started.
                        </TableCell>
                      </TableRow>
                    ) : (
                      accountManagers.map((manager) => (
                        <TableRow key={manager.id} className="border-border/50">
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {manager.picture ? (
                                <img
                                  src={manager.picture}
                                  alt={manager.name}
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                                  <span className="text-sm font-medium">
                                    {manager.name?.[0]?.toUpperCase() || "?"}
                                  </span>
                                </div>
                              )}
                              <span className="font-medium">{manager.name || "Unnamed"}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{manager.email}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                              {manager.assigned_clients_count} clients
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30">
                              {manager.total_videos_created} videos
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => navigate(`/admin/manager/${manager.id}/clients`)}>
                                  <Users className="w-4 h-4 mr-2" />
                                  View Clients
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-400"
                                  onClick={() => setRoleDialog({ userId: manager.id, currentRole: "account_manager", newRole: "user" })}
                                >
                                  <UserMinus className="w-4 h-4 mr-2" />
                                  Demote to User
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Unassigned Clients Tab */}
          <TabsContent value="unassigned" className="space-y-4">
            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle>Unassigned Clients</CardTitle>
                <CardDescription>
                  Users who are not currently assigned to any account manager
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50 hover:bg-transparent">
                      <TableHead>Client</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Videos</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {unassignedClients.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          All clients are assigned to account managers
                        </TableCell>
                      </TableRow>
                    ) : (
                      unassignedClients.map((client) => (
                        <TableRow key={client.id} className="border-border/50">
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {client.picture ? (
                                <img
                                  src={client.picture}
                                  alt={client.name}
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                                  <span className="text-sm font-medium">
                                    {client.name?.[0]?.toUpperCase() || "?"}
                                  </span>
                                </div>
                              )}
                              <span className="font-medium">{client.name || "Unnamed"}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{client.email}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{client.video_count} videos</Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDate(client.created_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              onClick={() => setAssignDialog(client)}
                              data-testid={`assign-client-${client.id}`}
                            >
                              <UserPlus className="w-4 h-4 mr-2" />
                              Assign
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Users Tab */}
          <TabsContent value="all-users" className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by email or name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-secondary/50 border-border/50"
                  data-testid="user-search"
                />
              </div>
            </div>

            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle>All Users</CardTitle>
                <CardDescription>
                  Manage user roles - promote users to Account Manager
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50 hover:bg-transparent">
                      <TableHead>User</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Clients</TableHead>
                      <TableHead>Videos</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow key={user.id} className="border-border/50">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {user.picture ? (
                              <img
                                src={user.picture}
                                alt={user.name}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                                <span className="text-sm font-medium">
                                  {user.name?.[0]?.toUpperCase() || "?"}
                                </span>
                              </div>
                            )}
                            <span className="font-medium">{user.name || "Unnamed"}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{user.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={roleColors[user.role] || roleColors.user}>
                            {user.role === "account_manager" ? "Account Manager" : 
                             user.role === "admin" ? "Admin" : "User"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {user.assigned_clients_count > 0 && (
                            <Badge variant="outline" className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                              {user.assigned_clients_count} clients
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-muted-foreground">{user.video_count}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {user.role === "user" && (
                                <DropdownMenuItem
                                  onClick={() => setRoleDialog({ userId: user.id, currentRole: user.role, newRole: "account_manager" })}
                                >
                                  <ShieldCheck className="w-4 h-4 mr-2" />
                                  Promote to Account Manager
                                </DropdownMenuItem>
                              )}
                              {user.role === "account_manager" && (
                                <>
                                  <DropdownMenuItem
                                    onClick={() => setRoleDialog({ userId: user.id, currentRole: user.role, newRole: "admin" })}
                                  >
                                    <Shield className="w-4 h-4 mr-2" />
                                    Promote to Admin
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="text-yellow-400"
                                    onClick={() => setRoleDialog({ userId: user.id, currentRole: user.role, newRole: "user" })}
                                  >
                                    <UserMinus className="w-4 h-4 mr-2" />
                                    Demote to User
                                  </DropdownMenuItem>
                                </>
                              )}
                              {user.role === "admin" && (
                                <DropdownMenuItem
                                  className="text-yellow-400"
                                  onClick={() => setRoleDialog({ userId: user.id, currentRole: user.role, newRole: "account_manager" })}
                                >
                                  <UserMinus className="w-4 h-4 mr-2" />
                                  Demote to Account Manager
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Assign Client Dialog */}
      <Dialog open={!!assignDialog} onOpenChange={() => setAssignDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Client to Account Manager</DialogTitle>
            <DialogDescription>
              Select an account manager to handle {assignDialog?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedManager} onValueChange={setSelectedManager}>
              <SelectTrigger data-testid="select-manager">
                <SelectValue placeholder="Select account manager..." />
              </SelectTrigger>
              <SelectContent>
                {accountManagers.map((manager) => (
                  <SelectItem key={manager.id} value={manager.id}>
                    {manager.name} ({manager.email}) - {manager.assigned_clients_count} clients
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssignDialog(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleAssignClient}
              disabled={!selectedManager || actionLoading}
              data-testid="confirm-assign"
            >
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Assign Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Change Confirmation Dialog */}
      <Dialog open={!!roleDialog} onOpenChange={() => setRoleDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Role Change</DialogTitle>
            <DialogDescription>
              Are you sure you want to change this user's role from{" "}
              <strong>{roleDialog?.currentRole}</strong> to{" "}
              <strong>{roleDialog?.newRole}</strong>?
              {roleDialog?.newRole === "user" && roleDialog?.currentRole === "account_manager" && (
                <p className="mt-2 text-yellow-400">
                  Warning: All assigned clients will be unassigned from this user.
                </p>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRoleDialog(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateRole}
              disabled={actionLoading}
              variant={roleDialog?.newRole === "user" ? "destructive" : "default"}
              data-testid="confirm-role-change"
            >
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
